import { type VoidAsyncResult, createResolvedAsyncResult } from "@/cache/AsyncResult";
import { MARKER_BRAND_PACIFICBLUE } from "@/constants/custom-marker";
import { emptyFeatureCollection } from "@/geojson/empty";
import { IconStore, getIconMetaData } from "@/icon-store/IconStore";
import type { ScenePublicApi } from "@/layers/Compositor";
import type { SceneLayer } from "@/layers/LayerBase";
import { getDrawLayers } from "@/layers/geojson/drawing/drawing-layer-utilities";
import type { TabLocationMarkersLayer } from "@/map/location-markers/TabLocationMarkersLayer";
import { buildLocationMarkerFeatureCollection } from "@/map/location-markers/locationMarkers";
import { safeFmt } from "@/utility/safeFmt";
import Logger from "@mm/logging";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { DateTime } from "luxon";
import mapboxgl, { type GeoJSONSource, type GeoJSONSourceSpecification, type MapMouseEvent } from "mapbox-gl";

const MARKER_IMAGE_SIZE = 35;
const DEFAULT_ICON_IMAGE_SIZE = 64;
const MARKER_ICON_SCALE = MARKER_IMAGE_SIZE / DEFAULT_ICON_IMAGE_SIZE;
const MARKER_METADATA = getIconMetaData(MARKER_BRAND_PACIFICBLUE);

const logger = Logger.fromFilename(__filename);

type MarkerInfrastructureIds = {
  layerId: string;
  sourceId: string;
};

function buildMarkerSourceSpec(): GeoJSONSourceSpecification {
  return {
    type: "geojson",
    data: emptyFeatureCollection,
  };
}

function getMarkerInfrastructureIds(uid: number): MarkerInfrastructureIds {
  return {
    sourceId: `metx-synced-location-marker-source-${uid}`,
    layerId: `metx-synced-location-marker-layer-${uid}`,
  };
}

function buildFeatureCollection(layer: TabLocationMarkersLayer) {
  return layer.show ? buildLocationMarkerFeatureCollection(layer.markers) : emptyFeatureCollection;
}

export class TabLocationMarkersLayerImpl implements SceneLayer {
  public timezone: GuiTimeZone;
  private readonly infrastructureIds: MarkerInfrastructureIds;
  private popup: mapboxgl.Popup | null = null;
  private iconLoadPromise: Promise<void> | null = null;
  private disposed = false;

  constructor(
    public readonly uid: number,
    private props: TabLocationMarkersLayer,
    public readonly scene: ScenePublicApi,
    timezone: GuiTimeZone,
  ) {
    this.timezone = timezone;
    this.infrastructureIds = getMarkerInfrastructureIds(uid);
    this.syncInfrastructure();
    this.addEventListeners();
  }

  humanReadableId(): string {
    return safeFmt`metx.TabLocationMarkersLayer#${this.uid}`;
  }

  get zIndex(): number {
    return this.props.index;
  }

  get mapboxIndex(): string {
    return this.infrastructureIds.layerId;
  }

  setTimezone(value: GuiTimeZone) {
    this.timezone = value;
  }

  moveZIndex(beforeLayerId?: string): void {
    const map = this.scene.getMapboxMap();
    if (!map.getLayer(this.infrastructureIds.layerId)) {
      return;
    }

    const firstDrawLayerId = getDrawLayers(map)[0];
    const nextLayerId = firstDrawLayerId ?? beforeLayerId;
    if (nextLayerId && map.getLayer(nextLayerId)) {
      map.moveLayer(this.infrastructureIds.layerId, nextLayerId);
    }
  }

  getLayerProps(): TabLocationMarkersLayer {
    return this.props;
  }

  getZoomLevelBias() {
    return 0;
  }

  triggerRefetchData() {}

  getAppliedStyle() {
    return undefined;
  }

  getAppliedEnsSelect() {
    return "";
  }

  checkChanges(layerDesc: TabLocationMarkersLayer): boolean {
    const markersChanged = this.props.markers !== layerDesc.markers;
    const visibilityChanged = this.props.show !== layerDesc.show;
    const indexChanged = this.props.index !== layerDesc.index;

    this.props = layerDesc;

    if (markersChanged || visibilityChanged) {
      this.syncInfrastructure();
    }

    return !(markersChanged || visibilityChanged || indexChanged);
  }

  beforeRender(): void {}

  removeLayer(): void {
    this.disposed = true;
    const map = this.scene.getMapboxMap();
    this.popup?.remove();
    this.popup = null;
    map.off("styledata", this.handleStyleData);
    map.off("click", this.handleMapClick);
    map.off("mousemove", this.handleMouseMove);
    map.getCanvas().style.cursor = "";
    if (map.getLayer(this.infrastructureIds.layerId)) {
      map.removeLayer(this.infrastructureIds.layerId);
    }
    if (map.getSource(this.infrastructureIds.sourceId)) {
      map.removeSource(this.infrastructureIds.sourceId);
    }
  }

  fetchData(_: DateTime): VoidAsyncResult {
    return createResolvedAsyncResult(undefined);
  }

  isRebuffering(_: DateTime): boolean {
    return false;
  }

  getActiveWeatherParametersAsString() {
    return [];
  }

  private addEventListeners() {
    const map = this.scene.getMapboxMap();
    map.on("styledata", this.handleStyleData);
    map.on("click", this.handleMapClick);
    map.on("mousemove", this.handleMouseMove);
  }

  private readonly handleStyleData = () => {
    this.syncInfrastructure();
  };

  private readonly handleMapClick = (event: MapMouseEvent) => {
    const map = this.scene.getMapboxMap();
    if (!map.getLayer(this.infrastructureIds.layerId)) {
      return;
    }

    const feature = map.queryRenderedFeatures(event.point, { layers: [this.infrastructureIds.layerId] })[0];
    const coordinates = feature?.geometry?.type === "Point" ? feature.geometry.coordinates : null;
    const popupHtml = typeof feature?.properties?.popupHtml === "string" ? feature.properties.popupHtml : null;
    if (!coordinates || typeof popupHtml !== "string") {
      return;
    }

    this.popup?.remove();
    this.popup = new mapboxgl.Popup({
      offset: 20,
      maxWidth: "420px",
      className: "mapbox-popover mapbox-popover-nwa",
    })
      .setLngLat([coordinates[0], coordinates[1]])
      .setHTML(popupHtml)
      .addTo(map);
  };

  private readonly handleMouseMove = (event: MapMouseEvent) => {
    const map = this.scene.getMapboxMap();
    const features = map.getLayer(this.infrastructureIds.layerId)
      ? map.queryRenderedFeatures(event.point, { layers: [this.infrastructureIds.layerId] })
      : [];
    map.getCanvas().style.cursor = features.length > 0 ? "pointer" : "";
  };

  private ensureInfrastructure() {
    const map = this.scene.getMapboxMap();

    if (!map.getSource(this.infrastructureIds.sourceId)) {
      map.addSource(this.infrastructureIds.sourceId, buildMarkerSourceSpec());
    }

    if (!map.getLayer(this.infrastructureIds.layerId)) {
      map.addLayer({
        id: this.infrastructureIds.layerId,
        type: "symbol",
        source: this.infrastructureIds.sourceId,
        layout: {
          "icon-image": MARKER_BRAND_PACIFICBLUE,
          "icon-size": MARKER_ICON_SCALE,
          "icon-anchor": "bottom",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          visibility: this.props.show ? "visible" : "none",
        },
      });
    }

    this.moveZIndex();
  }

  private ensureMarkerIcon(): Promise<void> | null {
    const map = this.scene.getMapboxMap();
    if (map.hasImage(MARKER_BRAND_PACIFICBLUE)) {
      return null;
    }

    if (!this.iconLoadPromise) {
      this.iconLoadPromise = IconStore.instance
        .getIcon(MARKER_BRAND_PACIFICBLUE)
        .then((iconImage) => {
          if (this.disposed || map.hasImage(MARKER_BRAND_PACIFICBLUE)) {
            return;
          }
          map.addImage(MARKER_BRAND_PACIFICBLUE, iconImage as HTMLImageElement, {
            sdf: MARKER_METADATA.sdf,
            pixelRatio: window.devicePixelRatio,
          });
        })
        .catch((error) => {
          logger.error(error);
        })
        .finally(() => {
          this.iconLoadPromise = null;
        });
    }

    return this.iconLoadPromise;
  }

  private syncInfrastructure() {
    if (this.disposed) {
      return;
    }

    const iconPromise = this.ensureMarkerIcon();
    if (iconPromise) {
      void iconPromise.then(() => {
        if (this.disposed) {
          return;
        }
        this.ensureInfrastructure();
        this.setSourceData();
        this.updateLayerVisibility();
      });
      return;
    }

    this.ensureInfrastructure();
    this.setSourceData();
    this.updateLayerVisibility();
    this.moveZIndex();
  }

  private setSourceData() {
    const source = this.scene.getMapboxMap().getSource(this.infrastructureIds.sourceId) as GeoJSONSource | undefined;
    source?.setData(buildFeatureCollection(this.props));
  }

  private updateLayerVisibility() {
    const map = this.scene.getMapboxMap();
    if (map.getLayer(this.infrastructureIds.layerId)) {
      map.setLayoutProperty(this.infrastructureIds.layerId, "visibility", this.props.show ? "visible" : "none");
    }
  }
}
